import { ref, onMounted } from 'vue';
import { C_REG_MERGE, isIndividualRegex } from '_acaSrc/utility/constants';
import { getQueryParamValues } from '_acaSrc/utility/http';
import { getWindow } from '_acaSrc/utility/DOM';
import UtdCache from '_acaSrc/utility/UtdCache';
import PubSub from '_acaSrc/utility/PubSub';
import useRegistrationForm from '_acaSrc/composables/registrationForm.composable';

const pubsub = new PubSub();

const MERGE_SOURCES = Object.freeze({
    AUTOMERGE: 'AUTOMERGE',
    CONSOLIDATE: 'CONSOLIDATE'
});

const hasMergeSuccess = ref(false);
const mergeSource = ref(MERGE_SOURCES.AUTOMERGE);
const mergeType = ref(null);
const isIndividualSubscriber = ref(false);

export default function useRegisterMergeStatus() {

    const clearDestinationUrlCache = () => {
        const utdCache = new UtdCache();
        const destinationUrl = utdCache.getPersistent('utdDestinationUrl');
        
        if (destinationUrl?.includes(C_REG_MERGE.REG_MERGE_SUCCESS_PARAM)) {
            utdCache.removePersistent('utdDestinationUrl');
        }
    };

    const getMergeResultType = () => {
        const url = getWindow().location.href;
        return getQueryParamValues(url, C_REG_MERGE.REG_MERGE_SUCCESS_PARAM);
    };

    const extractSourceAndType = (resultType) => {
        if (!resultType) {
            return { baseType: null, source: MERGE_SOURCES.AUTOMERGE };
        }

        const consolidateSuffix = '_CONSOLIDATE';
        
        if (resultType.endsWith(consolidateSuffix)) {
            const baseType = resultType.slice(0, -consolidateSuffix.length);
            return { baseType, source: MERGE_SOURCES.CONSOLIDATE };
        }

        return { baseType: resultType, source: MERGE_SOURCES.AUTOMERGE };
    };

    const determineMergeType = (resultType, serverTag) => {
        const isSsoMerge = resultType === C_REG_MERGE.MESSAGES.SSO_MERGE.name;
        const isIndividualUser = isIndividualRegex.test(serverTag);
        isIndividualSubscriber.value = isIndividualUser;
        
        if (isSsoMerge && isIndividualUser) {
            return C_REG_MERGE.MESSAGES.GENERIC_MERGE.name;
        }
        
        return resultType;
    };

    const getMergeSuccessMessage = (serverTag) => {
        const resultType = getMergeResultType();
        const { baseType, source } = extractSourceAndType(resultType);
        
        if (!baseType) {
            return null;
        }

        mergeSource.value = source;
        
        mergeType.value = determineMergeType(baseType, serverTag);
        return C_REG_MERGE.MESSAGES[mergeType.value];
    };

    const handleMergeSuccess = (serverTag) => {
        clearDestinationUrlCache();
        
        const successMessage = getMergeSuccessMessage(serverTag);
        
        if (successMessage) {
            const { setSuccessMessage } = useRegistrationForm();
            setSuccessMessage(successMessage);
            hasMergeSuccess.value = true;
            pubsub.publish('showSuccessNotification');
        }
    };

    const initializeMergeStatus = (serverTag) => {
        onMounted(() => {
            handleMergeSuccess(serverTag);
        });
    };

    const resetMergeStatus = () => {
        hasMergeSuccess.value = false;
        mergeSource.value = MERGE_SOURCES.AUTOMERGE;
        mergeType.value = null;
        isIndividualSubscriber.value = false;
    };

    return {
        hasMergeSuccess,
        isIndividualSubscriber,
        mergeSource,
        mergeType,
        MERGE_SOURCES,
        handleMergeSuccess,
        initializeMergeStatus,
        getMergeSuccessMessage,
        resetMergeStatus
    };
}
